<template>
  <v-container class="pa-0 ma-0 d-flex flex-column gap-1">
    <!-- cards-->
    <v-row class="ma-0 pa-0 gap-2">
      <v-col
        v-for="(item, index) in stats"
        :key="index"
        cols="auto"
        class="ma-0 pa-0 flex-fill"
      >
        <v-card
          outlined
          :style="getIconName(item.title).bgColor"
          class="stat-cards ma-0 pa-3 d-flex flex-column gap-2"
        >
          <!-- title + icon -->
          <v-container class="pa-0 ma-0 d-flex justify-space-between align-center">
            <v-container class="pa-0 ma-0 d-flex flex-column">
              <!-- title -->
              <v-card-text class="pa-0 ma-0 text-body-1">
                {{ item.title }}
              </v-card-text>
              <!-- value -->
              <v-card-text class="pa-0 ma-0 text-body-2">
                {{ item.value }}
                <span v-if="item.title === $t('invoiceCount')">
                  {{ $t("numbers") }}
                </span>
                <span v-else> {{ $store.state.currency.name }} </span>
              </v-card-text>
            </v-container>
            <v-sheet
              width="28"
              height="28"
              :color="getIconName(item.title).bgIcon"
              class="d-flex align-center justify-center ml-3"
            >
              <my-icon
                :name="getIconName(item.title).name"
                :width="14"
                :height="14"
                :stroke="getIconName(item.title).color"
                :fill="getIconName(item.title).color"
              />
            </v-sheet>
          </v-container>
          <!-- percent -->
          <v-container class="pa-0 ma-0 d-flex align-center">
            <v-icon size="14" :color="getPercentColor(item.trend)" class="ma-0 pa-0">
              {{ getPercentIcon(item.trend) }}
            </v-icon>
            <v-container class="pa-0 ma-0 d-flex">
              <v-card-text
                class="pa-0 ma-0 pl-2 text-body-2"
                :style="{ color: getPercentColor(item.trend) }"
              >
                {{ item.percent }}٪
              </v-card-text>

              <v-card-text class="pa-0 ma-0 text-body-2">
                {{ item.compare }}
              </v-card-text>
            </v-container>
          </v-container>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import MyIcon from "@/views/dashboard/components/general/icon/MyIcon.vue";

export default {
  name: "DashboardStats",
  components: {
    MyIcon,
  },
  props: {
    profitData: {
      type: [Number, String, Object],
      required: true,
    },
    sellData: {
      type: [Number, String, Object],
      required: true,
    },
    invoiceData: {
      type: [Array, Object],
      required: true,
    },
    duration: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {};
  },
  computed: {
    averageInvoice() {
      const sell = this.sellData?.current ?? 0;
      const invoiceCount = this.invoiceData?.current ?? 0;
      if (!invoiceCount) return 0;
      const avg = sell / invoiceCount;
      return this.roundTo(avg, 4);
    },
    averageInvoicePercent() {
      const sellDiff = this.sellData?.diffPercent ?? 0;
      const invoiceDiff = this.invoiceData?.diffPercent ?? 0;
      let diff = sellDiff - invoiceDiff;
      return this.roundTo(diff, 2);
    },
    averageInvoiceTrend() {
      return this.averageInvoicePercent >= 0 ? "increase" : "decrease";
    },

    stats() {
      return [
        {
          title: this.$t("total_sales"),
          value: this.formatNumber(this.sellData?.current ?? 0),
          percent: this.sellData?.diffPercent ?? 0,
          trend: this.sellData?.trend ?? "none",
          compare: this.duration ? this.$t("duration") : this.$t("month_compare"),
        },
        {
          title: this.$t("net_profit"),
          value: this.formatNumber(this.profitData?.current ?? 0),
          percent: this.profitData?.diffPercent ?? 0,
          trend: this.profitData?.trend ?? "none",
          compare: this.duration ? this.$t("duration") : this.$t("month_compare"),
        },
        {
          title: this.$t("invoiceCount"),
          value: this.invoiceData?.current ?? 0,
          percent: this.invoiceData?.diffPercent ?? 0,
          trend: this.invoiceData?.trend ?? "none",
          compare: this.duration ? this.$t("duration") : this.$t("month_compare"),
        },
        {
          title: this.$t("average_invoice"),
          value: this.formatNumber(this.averageInvoice),
          percent: Math.abs(this.averageInvoicePercent),
          trend: this.averageInvoiceTrend,
          compare: this.duration ? this.$t("duration") : this.$t("month_compare"),
        },
      ];
    },
  },

  watch: {
    duration(newVal, oldVal) {},
  },
  methods: {
    roundTo(value, decimals = 4) {
      if (!value && value !== 0) return 0;
      return Number(Math.round(value + "e" + decimals) + "e-" + decimals);
    },
    formatNumber(value) {
      if (value === null || value === undefined) return "-";
      return this.$numberFormat(value);
    },
    getIconName(title) {
      if (title === this.$t("total_sales"))
        return {
          name: "currency-icon",
          color: "#fff",
          bgIcon: "#155DFC",
          bgColor: {
            backgroundColor: "#2b80ff24",
          },
        };
      if (title === this.$t("net_profit"))
        return {
          name: "arrow-up",
          color: "#fff",
          bgIcon: "#00A63E",
          bgColor: {
            backgroundColor: "#00c9501b",
          },
        };
      if (title === this.$t("invoiceCount"))
        return {
          name: "bag-shop",
          color: "#fff",
          bgIcon: "#F54900",
          bgColor: {
            backgroundColor: "#ff6a003d",
          },
        };
      if (title === this.$t("average_invoice"))
        return {
          name: "medal",
          color: "#fff",
          bgIcon: "#9810FA",
          bgColor: {
            backgroundColor: "#ac46ff48",
          },
        };
    },
    getPercentColor(trend) {
      if (trend != null && trend == "increase") {
        return "#008236";
      }
      if (trend != null && trend == "decrease") {
        return "#C10007";
      }
      if (trend != null && trend == "none") {
        return "#a7a4a4ff";
      }
    },
    getPercentIcon(trend) {
      if (trend != null && trend == "increase") {
        return "mdi-arrow-top-right";
      }
      if (trend != null && trend == "decrease") {
        return "mdi-arrow-bottom-right";
      }
      if (trend != null && trend == "none") {
        return "";
      }
    },
  },
};
</script>

<style scoped>
.dashboard-wrapper {
  border-radius: 4px;
  border: solid #000 1px;
  background: linear-gradient(135deg, #155dfc, #1447e6, #6e11b0);
}
.stat-cards {
  border-radius: 12px !important;
  border: 1px solid #f3f4f6;
  backdrop-filter: blur(5px);
}
</style>
